import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Sparkles, 
  Settings, 
  ChevronDown,
  ChevronUp,
  Info,
  CheckCircle,
  Loader2
} from 'lucide-react'
import { useCreateSwarm } from '../hooks/useSwarms'
import { useAuthStore } from '../stores/auth'
import { useModels, getSwarmModels, getDefaultModelId, getModelDescription } from '../hooks/useModels'

const AVAILABLE_TOOLS = [
  { id: 'web_search', name: 'Web Search', description: 'Search the web for information' },
  { id: 'browser', name: 'Browser', description: 'Browse websites and extract content' },
  { id: 'code_execution', name: 'Code Execution', description: 'Execute code safely' },
]

export default function CreateSwarm() {
  const navigate = useNavigate()
  const createSwarm = useCreateSwarm()
  const { tenant } = useAuthStore()
  const { data: modelsData, isLoading: modelsLoading } = useModels()
  
  const [step, setStep] = useState(1)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task: '',
    config: {
      maxAgents: tenant?.settings?.maxAgentsPerSwarm || 10,
      model: 'grok-4-1-fast',
      temperature: 0.7,
      tools: ['web_search'],
      enableFactChecking: true,
      enableSelfImprovement: true,
    },
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Update default model when models are loaded
  useEffect(() => {
    if (modelsData?.models && modelsData.models.length > 0) {
      const swarmModels = getSwarmModels(modelsData.models)
      const defaultModel = getDefaultModelId(swarmModels)
      setFormData(prev => ({
        ...prev,
        config: { ...prev.config, model: defaultModel }
      }))
    }
  }, [modelsData])

  const swarmModels = modelsData ? getSwarmModels(modelsData.models) : []

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!formData.task.trim()) {
      newErrors.task = 'Task is required'
    } else if (formData.task.length < 20) {
      newErrors.task = 'Task must be at least 20 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateStep1()) return

    const result = await createSwarm.mutateAsync({
      name: formData.name,
      description: formData.description,
      task: formData.task,
      config: formData.config,
    })

    if (result?.swarm?.id) {
      navigate(`/swarms/${result.swarm.id}`)
    }
  }

  const selectedModel = swarmModels.find(m => m.id === formData.config.model)

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Swarm</h1>
          <p className="text-sm text-gray-500">Configure and launch your agent swarm</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
            step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            1
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`} />
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
            step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            2
          </div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-primary-600' : 'bg-gray-200'}`} />
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
            step >= 3 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            3
          </div>
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>Basic Info</span>
          <span>Configuration</span>
          <span>Review</span>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Swarm Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Market Research Analysis"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this swarm's purpose"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Description *
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Describe what you want the swarm to accomplish. Be specific and detailed.
              </p>
              <textarea
                value={formData.task}
                onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                rows={6}
                placeholder="Example: Research the top 10 AI companies in healthcare for 2025. For each company, find their funding, key products, and competitive advantages. Create a comprehensive report with analysis."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.task && (
                <p className="mt-1 text-sm text-red-600">{errors.task}</p>
              )}
              <p className="mt-1 text-xs text-gray-400 text-right">
                {formData.task.length} characters (min 20)
              </p>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  if (validateStep1()) setStep(2)
                }}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Configuration */}
        {step === 2 && (
          <div className="p-6 space-y-6">
            {/* Max Agents */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Agents
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min={1}
                  max={tenant?.settings?.maxAgentsPerSwarm || 10}
                  value={formData.config.maxAgents}
                  onChange={(e) => setFormData({
                    ...formData,
                    config: { ...formData.config, maxAgents: parseInt(e.target.value) }
                  })}
                  className="flex-1"
                />
                <span className="w-12 text-center font-medium">{formData.config.maxAgents}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                More agents = faster execution but higher cost. Your plan allows up to {tenant?.settings?.maxAgentsPerSwarm || 10} agents.
              </p>
            </div>

            {/* Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Model
              </label>
              {modelsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading models...</span>
                </div>
              ) : swarmModels.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No models available
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {swarmModels.map((model) => (
                    <label
                      key={model.id}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.config.model === model.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="model"
                        value={model.id}
                        checked={formData.config.model === model.id}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, model: e.target.value }
                        })}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{model.name}</p>
                          <span className="text-xs text-gray-500">
                            {model.contextWindow >= 1000000 
                              ? `${(model.contextWindow / 1000000).toFixed(1)}M` 
                              : `${(model.contextWindow / 1000).toFixed(0)}K`} context
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{getModelDescription(model)}</p>
                        {model.isReasoning && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-2">
                            Reasoning
                          </span>
                        )}
                      </div>
                      {formData.config.model === model.id && (
                        <CheckCircle className="h-5 w-5 text-primary-600 ml-3" />
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Tools */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Tools
              </label>
              <div className="space-y-2">
                {AVAILABLE_TOOLS.map((tool) => (
                  <label
                    key={tool.id}
                    className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={formData.config.tools.includes(tool.id)}
                      onChange={(e) => {
                        const newTools = e.target.checked
                          ? [...formData.config.tools, tool.id]
                          : formData.config.tools.filter((t) => t !== tool.id)
                        setFormData({
                          ...formData,
                          config: { ...formData.config, tools: newTools }
                        })
                      }}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">{tool.name}</p>
                      <p className="text-xs text-gray-500">{tool.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center text-sm font-medium text-gray-700"
              >
                <Settings className="h-4 w-4 mr-2" />
                Advanced Settings
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-2" />
                )}
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-4 pl-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temperature
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min={0}
                        max={2}
                        step={0.1}
                        value={formData.config.temperature}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, temperature: parseFloat(e.target.value) }
                        })}
                        className="flex-1"
                      />
                      <span className="w-12 text-center font-medium">{formData.config.temperature}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Lower = more focused, Higher = more creative
                    </p>
                  </div>

                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.config.enableFactChecking}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, enableFactChecking: e.target.checked }
                        })}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Enable fact checking</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.config.enableSelfImprovement}
                        onChange={(e) => setFormData({
                          ...formData,
                          config: { ...formData.config, enableSelfImprovement: e.target.checked }
                        })}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Self-improvement loop</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Review
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-blue-900">Ready to Launch</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Review your configuration below before starting the swarm.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Name</span>
                <span className="font-medium text-gray-900">{formData.name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Description</span>
                <span className="font-medium text-gray-900 text-right max-w-xs">
                  {formData.description || '-'}
                </span>
              </div>
              <div className="py-2 border-b border-gray-100">
                <span className="text-gray-500 block mb-2">Task</span>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{formData.task}</p>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Max Agents</span>
                <span className="font-medium text-gray-900">{formData.config.maxAgents}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Model</span>
                <span className="font-medium text-gray-900">
                  {selectedModel?.name || formData.config.model}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Tools</span>
                <span className="font-medium text-gray-900">
                  {formData.config.tools.join(', ')}
                </span>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={createSwarm.isPending}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
              >
                {createSwarm.isPending ? (
                  <>
                    <div className="spinner h-4 w-4 mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Launch Swarm
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
